import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { KeyRound, Plus, Trash2, Copy, ShieldAlert, ShieldCheck, Sparkles, Send as SendIcon, AlertTriangle, Package, CreditCard } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";

// ─────────────── Overview ───────────────
export function OtpOverview() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/otp/overview").then(r => setData(r.data)).catch(() => {}); }, []);
  const kpi = data?.kpis || {};
  const sub = data?.subscription;
  const plan = data?.plan;

  return (
    <div className="space-y-4" data-testid="otp-overview">
      <div>
        <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">OTP / API</div>
        <h1 className="text-3xl font-black tracking-tighter">Overview</h1>
        <p className="text-xs text-muted-foreground mt-1">Send WhatsApp OTPs from your own connected number, billed against your subscription.</p>
      </div>

      {data?.live_verification_status && (
        <Card className="rounded-sm shadow-none border-amber-300 dark:border-amber-800 bg-amber-50/60 dark:bg-amber-900/10">
          <CardContent className="p-3 flex items-start gap-2 text-xs">
            <AlertTriangle className="h-4 w-4 text-amber-700 shrink-0 mt-0.5" />
            <div><b>Live verification pending.</b> {data.live_verification_status}</div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { k: "today", label: "OTPs today" },
          { k: "month", label: "This month" },
          { k: "verified", label: "Verified" },
          { k: "verify_rate", label: "Verify rate", suffix: "%" },
        ].map(({ k, label, suffix }) => (
          <Card key={k} className="rounded-sm shadow-none">
            <CardContent className="p-4">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
              <div className="text-3xl font-black tracking-tight" data-testid={`kpi-${k}`}>{kpi[k] ?? 0}{suffix || ""}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Card className="rounded-sm shadow-none">
          <CardContent className="p-4 space-y-2">
            <div className="flex items-center gap-2 text-sm font-semibold"><Package className="h-4 w-4" /> Current subscription</div>
            {sub ? (
              <div className="space-y-1 text-sm">
                <div className="flex items-center justify-between"><span>{plan?.name || "Plan"}</span>
                  <Badge variant="outline" className={`rounded-sm text-[10px] ${sub.status === "active" ? "border-emerald-300 text-emerald-700" : "border-amber-300 text-amber-700"}`}>{(sub.status || "").toUpperCase()}</Badge></div>
                <div className="text-xs text-muted-foreground">Included credits: <b>{sub.included_quota_remaining}</b> / {sub.included_quota_total}</div>
                <div className="text-xs text-muted-foreground">Renews: {sub.renewal_at ? new Date(sub.renewal_at).toLocaleDateString() : "—"}</div>
              </div>
            ) : (
              <div className="text-xs">
                <div className="text-muted-foreground mb-2">You don't have an OTP plan yet.</div>
                <Link to="/otp/plans"><Button size="sm" className="rounded-sm gap-1"><CreditCard className="h-3 w-3" /> Buy a plan</Button></Link>
              </div>
            )}
          </CardContent>
        </Card>
        <Card className="rounded-sm shadow-none">
          <CardContent className="p-4 space-y-1 text-sm">
            <div className="flex items-center gap-2 font-semibold"><KeyRound className="h-4 w-4" /> Active API keys</div>
            <div className="text-3xl font-black">{kpi.active_keys ?? 0}</div>
            <Link to="/otp/keys"><Button size="sm" variant="outline" className="rounded-sm">Manage keys</Button></Link>
          </CardContent>
        </Card>
      </div>

      <Card className="rounded-sm shadow-none">
        <CardContent className="p-4">
          <div className="text-sm font-semibold mb-2">OTP volume — last 14 days</div>
          <div className="flex items-end gap-1 h-32">
            {(data?.volume_14d || []).map(v => {
              const max = Math.max(1, ...(data?.volume_14d || []).map(x => x.count || 0));
              const h = (v.count / max) * 100;
              return (
                <div key={v.date} className="flex-1 flex flex-col items-center gap-1">
                  <div className="w-full bg-emerald-200 dark:bg-emerald-900/40 rounded-t-sm" style={{ height: `${h}%`, minHeight: v.count ? "4px" : "1px" }} title={`${v.date}: ${v.count}`} />
                  <div className="text-[9px] text-muted-foreground">{v.date.slice(-2)}</div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ─────────────── Plans ───────────────
export function OtpPlans() {
  const [plans, setPlans] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/otp/plans").then(r => setPlans(r.data));
  useEffect(() => { load(); }, []);

  const buy = async (p) => {
    setBusy(true);
    try {
      const { data } = await api.post("/otp/subscription/checkout", { plan_id: p.id });
      // Load Razorpay checkout
      if (!window.Razorpay) {
        await new Promise((res, rej) => {
          const s = document.createElement("script"); s.src = "https://checkout.razorpay.com/v1/checkout.js";
          s.onload = res; s.onerror = rej; document.body.appendChild(s);
        });
      }
      const rzp = new window.Razorpay({
        key: data.key_id, amount: data.amount_paise, currency: "INR",
        name: "TezSandesh", description: `OTP Plan: ${data.plan_name}`,
        order_id: data.order_id,
        handler: async (resp) => {
          try {
            const v = await api.post("/otp/subscription/verify", {
              plan_id: p.id,
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            toast.success("OTP plan activated!");
            load();
          } catch (e) { toast.error(e.response?.data?.detail || "Verification failed"); }
        },
        theme: { color: "#0f172a" },
      });
      rzp.open();
    } catch (e) { toast.error(e.response?.data?.detail || "Checkout failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="otp-plans">
      <div>
        <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">OTP / API</div>
        <h1 className="text-3xl font-black tracking-tighter">OTP Plans</h1>
        <p className="text-xs text-muted-foreground mt-1">Pick a plan; pay securely via Razorpay. Activation happens only after verified payment.</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {plans.map(p => (
          <Card key={p.id} className="rounded-sm shadow-none" data-testid={`plan-${p.id}`}>
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="font-semibold">{p.name}</span>
                <Badge variant="outline" className="rounded-sm text-[10px]">{p.status}</Badge>
              </div>
              <div><span className="text-3xl font-black">₹{(p.monthly_price_paise / 100).toLocaleString()}</span><span className="text-xs text-muted-foreground"> / month</span></div>
              <div className="text-xs text-muted-foreground">{p.description}</div>
              <ul className="text-xs space-y-1">
                <li>✓ <b>{p.included_otp_count.toLocaleString()}</b> included OTPs</li>
                <li>✓ ₹{(p.overage_price_paise / 100).toFixed(2)} / OTP overage</li>
                <li>✓ {p.rate_limit_per_minute}/min · {p.rate_limit_per_day.toLocaleString()}/day</li>
                <li>✓ {p.maximum_api_keys} API keys · {p.maximum_connected_numbers} numbers</li>
                <li>✓ {p.log_retention_days}-day logs</li>
                {p.priority_support && <li>✓ Priority support</li>}
              </ul>
              <Button className="rounded-sm w-full gap-2" onClick={() => buy(p)} disabled={busy} data-testid={`buy-${p.id}`}>
                <CreditCard className="h-3 w-3" /> Buy plan
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

// ─────────────── API Keys ───────────────
export function OtpApiKeys() {
  const [keys, setKeys] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", environment: "test", scopes: ["otp:send", "otp:verify", "otp:status"] });
  const [newly, setNewly] = useState(null);
  const load = () => api.get("/otp/keys").then(r => setKeys(r.data));
  useEffect(() => { load(); }, []);
  const create = async () => {
    try { const { data } = await api.post("/otp/keys", form); setNewly(data); setOpen(false); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const revoke = async (id) => { if (!window.confirm("Revoke this OTP API key?")) return; await api.delete(`/otp/keys/${id}`); load(); };
  const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Copied"); };

  return (
    <div className="space-y-4" data-testid="otp-api-keys">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">OTP / API</div>
          <h1 className="text-3xl font-black tracking-tighter">OTP API Keys</h1>
          <p className="text-xs text-muted-foreground mt-1">Send from your own website/app: <code>Authorization: Bearer ts_live_…</code></p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="rounded-sm gap-2" data-testid="new-otp-key-button"><Plus className="h-4 w-4" /> New API key</Button></DialogTrigger>
          <DialogContent className="rounded-sm">
            <DialogHeader><DialogTitle>Create OTP API Key</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Name</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Production Backend" className="rounded-sm" data-testid="otp-key-name" /></div>
              <div><Label>Environment</Label>
                <select value={form.environment} onChange={e => setForm({ ...form, environment: e.target.value })} className="h-9 w-full px-3 rounded-sm border border-border bg-background text-sm" data-testid="otp-key-env">
                  <option value="test">test — safe sandbox, no live subscription needed</option>
                  <option value="live">live — requires active subscription</option>
                </select>
              </div>
            </div>
            <DialogFooter><Button onClick={create} className="rounded-sm" data-testid="otp-key-create">Create</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {newly && (
        <Card className="rounded-sm shadow-none border-amber-300 dark:border-amber-800 bg-amber-50/60 dark:bg-amber-900/10">
          <CardContent className="p-4 space-y-2">
            <div className="flex items-center gap-2 text-sm font-semibold text-amber-900 dark:text-amber-300"><ShieldAlert className="h-4 w-4" /> Copy this key now — you will not see it again</div>
            <div className="flex items-center gap-2">
              <code className="flex-1 font-mono text-xs bg-background border border-border rounded-sm p-2 break-all" data-testid="new-otp-key-value">{newly.full_key}</code>
              <Button size="icon" variant="outline" className="h-8 w-8 rounded-sm" onClick={() => copy(newly.full_key)}><Copy className="h-3.5 w-3.5" /></Button>
            </div>
            <Button variant="ghost" size="sm" className="rounded-sm" onClick={() => setNewly(null)}>Dismiss</Button>
          </CardContent>
        </Card>
      )}

      <Card className="rounded-sm shadow-none">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/40"><tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground">
              <th className="px-3 py-2">Name</th><th className="px-3 py-2">Env</th><th className="px-3 py-2">Prefix</th>
              <th className="px-3 py-2">Last used</th><th className="px-3 py-2">Status</th><th className="px-3 py-2 text-right"></th></tr></thead>
            <tbody>{keys.map(k => (<tr key={k.id} className="border-t border-border" data-testid={`otp-key-${k.id}`}>
              <td className="px-3 py-2 font-medium">{k.name}</td>
              <td className="px-3 py-2"><Badge variant="outline" className={`rounded-sm text-[10px] ${k.environment === "live" ? "border-emerald-300 text-emerald-700" : "border-sky-300 text-sky-700"}`}>{k.environment.toUpperCase()}</Badge></td>
              <td className="px-3 py-2 font-mono text-xs">{k.key_prefix}</td>
              <td className="px-3 py-2 font-mono text-xs">{k.last_used_at ? new Date(k.last_used_at).toLocaleString() : "—"}</td>
              <td className="px-3 py-2">{k.revoked_at ? <Badge variant="outline" className="rounded-sm text-[10px] border-red-300 text-red-700">REVOKED</Badge> : <Badge variant="outline" className="rounded-sm text-[10px] border-emerald-300 text-emerald-700">ACTIVE</Badge>}</td>
              <td className="px-3 py-2 text-right">{!k.revoked_at && <Button variant="ghost" size="sm" className="rounded-sm text-red-600" onClick={() => revoke(k.id)}><Trash2 className="h-3 w-3" /></Button>}</td>
            </tr>))}{keys.length === 0 && <tr><td colSpan={6} className="text-center text-muted-foreground py-10">No OTP API keys yet.</td></tr>}</tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

// ─────────────── Send Test OTP ───────────────
export function OtpSendTest() {
  const [keys, setKeys] = useState([]);
  const [key, setKey] = useState("");
  const [env, setEnv] = useState("test");
  const [to, setTo] = useState("");
  const [purpose, setPurpose] = useState("login");
  const [reference, setReference] = useState("");
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [sent, setSent] = useState(null);
  const [statusData, setStatusData] = useState(null);
  const [verifyOtp, setVerifyOtp] = useState("");
  const [verifyRes, setVerifyRes] = useState(null);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get("/otp/keys").then(r => setKeys(r.data.filter(k => !k.revoked_at))).catch(() => {});
    api.get("/otp/templates").then(r => setTemplates(r.data.templates || [])).catch(() => {});
  }, []);

  const send = async () => {
    if (!key) return toast.error("Paste an OTP API key");
    if (!to) return toast.error("Recipient required");
    setSending(true); setSent(null); setVerifyRes(null); setStatusData(null);
    try {
      const url = process.env.REACT_APP_BACKEND_URL + "/api/v1/otp/send";
      const r = await fetch(url, {
        method: "POST",
        headers: { "Authorization": "Bearer " + key, "Content-Type": "application/json",
                   "Idempotency-Key": "test-" + Date.now() },
        body: JSON.stringify({ to, purpose, reference_id: reference || undefined }),
      });
      const d = await r.json();
      setSent({ ok: r.ok, status: r.status, body: d });
      if (r.ok) toast.success("OTP accepted (mode: " + d.mode + ")");
      else toast.error((d?.detail?.message) || d?.detail || "Send failed");
    } catch (e) { toast.error(String(e)); } finally { setSending(false); }
  };

  const refreshStatus = async () => {
    if (!sent?.body?.request_id) return;
    try {
      const url = process.env.REACT_APP_BACKEND_URL + `/api/v1/otp/status/${sent.body.request_id}`;
      const r = await fetch(url, { headers: { "Authorization": "Bearer " + key } });
      const d = await r.json();
      setStatusData(d);
    } catch (e) { toast.error(String(e)); }
  };

  const resend = async () => {
    if (!sent?.body?.request_id) return;
    try {
      const url = process.env.REACT_APP_BACKEND_URL + "/api/v1/otp/resend";
      const r = await fetch(url, {
        method: "POST",
        headers: { "Authorization": "Bearer " + key, "Content-Type": "application/json" },
        body: JSON.stringify({ request_id: sent.body.request_id }),
      });
      const d = await r.json();
      if (r.ok) { toast.success("Resent"); await refreshStatus(); }
      else toast.error((d?.detail?.message) || d?.detail || "Resend failed");
    } catch (e) { toast.error(String(e)); }
  };

  const verify = async () => {
    if (!sent?.body?.request_id) return;
    try {
      const url = process.env.REACT_APP_BACKEND_URL + "/api/v1/otp/verify";
      const r = await fetch(url, {
        method: "POST",
        headers: { "Authorization": "Bearer " + key, "Content-Type": "application/json" },
        body: JSON.stringify({ request_id: sent.body.request_id, to, otp: verifyOtp }),
      });
      const d = await r.json();
      setVerifyRes({ ok: r.ok, status: r.status, body: d });
    } catch (e) { toast.error(String(e)); }
  };

  // Detect env from pasted key
  const detectedEnv = key.startsWith("ts_live_") ? "live" : key.startsWith("ts_test_") ? "test" : env;

  return (
    <div className="space-y-4" data-testid="otp-send-test">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">OTP / API</div>
          <h1 className="text-3xl font-black tracking-tighter">Send Test OTP</h1>
          <p className="text-xs text-muted-foreground mt-1">Fire a real request through your OTP API key to test the full send → verify flow.</p>
        </div>
        <Badge variant="outline" className={`rounded-sm text-[10px] uppercase ${detectedEnv === "live" ? "border-emerald-300 text-emerald-700" : "border-sky-300 text-sky-700"}`} data-testid="detected-env-badge">
          {detectedEnv === "live" ? "LIVE" : "TEST"}
        </Badge>
      </div>

      <Card className="rounded-sm shadow-none">
        <CardContent className="p-4 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div><Label>API Key <span className="text-[10px] text-muted-foreground">(paste the full <code>ts_test_…</code> or <code>ts_live_…</code>)</span></Label>
              <Input value={key} onChange={e => setKey(e.target.value)} placeholder="ts_test_xxxxxx_yyyyyy" className="rounded-sm font-mono text-xs" data-testid="test-otp-key" />
              {keys.length > 0 && <div className="text-[10px] text-muted-foreground mt-1">You have {keys.length} active key(s). Paste the full one you saved on creation.</div>}
            </div>
            <div><Label>Recipient (E.164)</Label><Input value={to} onChange={e => setTo(e.target.value)} placeholder="+919876543210" className="rounded-sm font-mono" data-testid="test-otp-to" /></div>
            <div><Label>Template <span className="text-[10px] text-muted-foreground">(info only — server uses active default)</span></Label>
              <select value={selectedTemplate} onChange={e => setSelectedTemplate(e.target.value)}
                className="w-full h-9 px-2 rounded-sm border border-border bg-background text-sm" data-testid="test-otp-template">
                <option value="">— use active default —</option>
                {templates.filter(t => (t.status || "").toUpperCase() === "APPROVED").map(t => (
                  <option key={t.id} value={t.name}>{t.name} ({t.language}) {t.is_default_otp ? "★" : ""}</option>
                ))}
              </select>
            </div>
            <div><Label>Purpose</Label><Input value={purpose} onChange={e => setPurpose(e.target.value)} className="rounded-sm" data-testid="test-otp-purpose" /></div>
            <div><Label>Reference ID (optional)</Label><Input value={reference} onChange={e => setReference(e.target.value)} className="rounded-sm" placeholder="user_123" data-testid="test-otp-ref" /></div>
          </div>
          <Button onClick={send} disabled={sending} className="rounded-sm gap-2" data-testid="test-otp-send"><SendIcon className="h-4 w-4" /> {sending ? "Sending…" : "Send OTP"}</Button>
          {sent && (
            <pre className={`text-[10px] font-mono p-2 rounded-sm border ${sent.ok ? "border-emerald-300 bg-emerald-50 dark:bg-emerald-900/10" : "border-red-300 bg-red-50 dark:bg-red-900/10"}`} data-testid="test-otp-result">
{JSON.stringify(sent.body, null, 2)}
            </pre>
          )}
        </CardContent>
      </Card>

      {sent?.ok && (
        <>
          <Card className="rounded-sm shadow-none">
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2 text-sm font-semibold"><ShieldCheck className="h-4 w-4" /> Delivery status</div>
                <div className="flex items-center gap-1">
                  <Button size="sm" variant="outline" className="rounded-sm gap-1" onClick={refreshStatus} data-testid="test-otp-refresh-status">
                    Refresh status
                  </Button>
                  <Button size="sm" variant="outline" className="rounded-sm gap-1" onClick={resend} data-testid="test-otp-resend">
                    Resend
                  </Button>
                </div>
              </div>
              {statusData ? (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                  {[
                    ["Status", statusData.status],
                    ["Sent", statusData.sent_at],
                    ["Delivered", statusData.delivered_at],
                    ["Read", statusData.read_at],
                    ["Verified", statusData.verified_at],
                    ["Meta ID", statusData.meta_message_id],
                    ["Mode", statusData.mode],
                    ["Resend #", statusData.resend_count],
                  ].map(([k, v]) => (
                    <div key={k} className="border border-border rounded-sm p-2">
                      <div className="text-[10px] uppercase text-muted-foreground">{k}</div>
                      <div className="text-xs font-mono">{v ? (String(v).length > 20 ? String(v).slice(0, 20) + "…" : String(v)) : "—"}</div>
                    </div>
                  ))}
                </div>
              ) : <div className="text-xs text-muted-foreground">Click "Refresh status" to check delivery updates from Meta.</div>}
            </CardContent>
          </Card>

          <Card className="rounded-sm shadow-none">
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center gap-2 text-sm font-semibold"><ShieldCheck className="h-4 w-4" /> Verify the received code</div>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <div className="md:col-span-3"><Label>Enter OTP</Label><Input value={verifyOtp} onChange={e => setVerifyOtp(e.target.value)} placeholder="123456" className="rounded-sm font-mono" data-testid="test-otp-verify-input" /></div>
                <div className="flex items-end"><Button onClick={verify} className="rounded-sm w-full" data-testid="test-otp-verify-button">Verify</Button></div>
              </div>
              {verifyRes && (
                <pre className={`text-[10px] font-mono p-2 rounded-sm border ${verifyRes.ok ? "border-emerald-300 bg-emerald-50 dark:bg-emerald-900/10" : "border-red-300 bg-red-50 dark:bg-red-900/10"}`} data-testid="test-otp-verify-result">
{JSON.stringify(verifyRes.body, null, 2)}
                </pre>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

// ─────────────── OTP Logs ───────────────
export function OtpLogs() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("");
  const [env, setEnv] = useState("");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const load = () => api.get("/otp/logs" + (status ? `?status=${status}` : "")).then(r => setItems(r.data));
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, [status]);

  const filtered = items.filter(r => {
    if (env && (r.environment || "").toLowerCase() !== env) return false;
    if (q) {
      const s = q.toLowerCase();
      if (!(String(r.to || "").toLowerCase().includes(s) ||
            String(r.reference_id || "").toLowerCase().includes(s) ||
            String(r.template_name || "").toLowerCase().includes(s) ||
            String(r.meta_message_id || "").toLowerCase().includes(s) ||
            String(r.id).toLowerCase().includes(s))) return false;
    }
    return true;
  });

  const exportCsv = async () => {
    try {
      const url = process.env.REACT_APP_BACKEND_URL + "/api/otp/logs.csv" + (status ? `?status=${status}` : "");
      const r = await fetch(url, { credentials: "include" });
      const blob = await r.blob();
      const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
      a.download = "otp-logs.csv"; a.click();
    } catch { toast.error("Export failed"); }
  };

  const S = ({ s, verified }) => {
    if (verified) return <Badge variant="outline" className="rounded-sm text-[10px] border-emerald-300 text-emerald-700">VERIFIED</Badge>;
    const cls = s === "delivered" ? "border-sky-300 text-sky-700"
      : s === "sent" ? "border-indigo-300 text-indigo-700"
      : s === "failed" ? "border-red-300 text-red-700"
      : s === "expired" ? "border-zinc-300 text-zinc-600"
      : "border-amber-300 text-amber-700";
    return <Badge variant="outline" className={`rounded-sm text-[10px] ${cls}`}>{(s || "").toUpperCase()}</Badge>;
  };

  return (
    <div className="space-y-4" data-testid="otp-logs">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">OTP / API</div>
          <h1 className="text-3xl font-black tracking-tighter">OTP Logs</h1>
          <p className="text-xs text-muted-foreground mt-1">Every OTP request from your API keys. Codes are never logged.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search phone, ref, template, meta id…" className="h-9 rounded-sm w-56" data-testid="otp-log-search" />
          <select value={env} onChange={e => setEnv(e.target.value)} className="h-9 px-3 rounded-sm border border-border bg-background text-sm" data-testid="otp-log-env">
            <option value="">All envs</option><option value="test">test</option><option value="live">live</option>
          </select>
          <select value={status} onChange={e => setStatus(e.target.value)} className="h-9 px-3 rounded-sm border border-border bg-background text-sm" data-testid="otp-log-filter">
            <option value="">All statuses</option><option>sent</option><option>delivered</option><option>verified</option><option>failed</option><option>expired</option>
          </select>
          <Button variant="outline" size="sm" className="rounded-sm gap-1" onClick={exportCsv} data-testid="otp-log-export">Export CSV</Button>
        </div>
      </div>
      <Card className="rounded-sm shadow-none">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/40"><tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground">
              <th className="px-3 py-2">Request</th><th className="px-3 py-2">Phone</th><th className="px-3 py-2">Purpose</th>
              <th className="px-3 py-2">Ref</th><th className="px-3 py-2">Template</th><th className="px-3 py-2">Env</th>
              <th className="px-3 py-2">Status</th><th className="px-3 py-2">Meta ID</th><th className="px-3 py-2 text-right">Created</th></tr></thead>
            <tbody>{filtered.map(r => (<tr key={r.id} className="border-t border-border hover:bg-muted/30 cursor-pointer" onClick={() => setSelected(r)} data-testid={`otp-log-${r.id}`}>
              <td className="px-3 py-2 font-mono text-[11px]">{r.id.slice(0, 14)}…</td>
              <td className="px-3 py-2 font-mono">{r.to}</td>
              <td className="px-3 py-2 text-xs">{r.purpose}</td>
              <td className="px-3 py-2 text-xs font-mono">{r.reference_id || "—"}</td>
              <td className="px-3 py-2 text-xs">{r.template_name}</td>
              <td className="px-3 py-2"><Badge variant="outline" className={`rounded-sm text-[10px] ${r.environment === "live" ? "border-emerald-300 text-emerald-700" : "border-sky-300 text-sky-700"}`}>{(r.environment || "").toUpperCase()}</Badge></td>
              <td className="px-3 py-2"><S s={r.status} verified={r.verified} /></td>
              <td className="px-3 py-2 font-mono text-[10px] text-muted-foreground">{(r.meta_message_id || "").slice(0, 22)}{r.meta_message_id && r.meta_message_id.length > 22 ? "…" : ""}</td>
              <td className="px-3 py-2 text-right text-xs font-mono">{new Date(r.created_at).toLocaleString()}</td>
            </tr>))}{filtered.length === 0 && <tr><td colSpan={9} className="text-center text-muted-foreground py-10">No OTP requests match.</td></tr>}</tbody>
          </table>
        </div>
      </Card>

      {/* Detail modal with timeline */}
      {selected && (
        <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
          <DialogContent className="rounded-sm max-w-2xl" data-testid="otp-log-detail">
            <DialogHeader><DialogTitle className="text-lg">OTP request · <span className="font-mono text-xs">{selected.id}</span></DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                {[["Phone", selected.to], ["Ref", selected.reference_id], ["Template", selected.template_name],
                  ["Env", selected.environment], ["Status", selected.status], ["Verified", String(!!selected.verified)],
                  ["Mode", selected.mode], ["Attempts", `${selected.attempts}/${selected.max_attempts || 5}`]].map(([k, v]) => (
                  <div key={k} className="border border-border rounded-sm p-2">
                    <div className="text-[10px] uppercase text-muted-foreground">{k}</div>
                    <div className="font-mono">{v || "—"}</div>
                  </div>
                ))}
              </div>
              <div className="text-sm font-semibold">Delivery timeline</div>
              <ol className="text-xs space-y-1">
                {[
                  ["Created", selected.created_at],
                  ["Sent", selected.sent_at],
                  ["Delivered", selected.delivered_at],
                  ["Read", selected.read_at],
                  ["Verified", selected.verified_at],
                  ["Failed", selected.failed_at],
                  ["Expires", selected.expires_at],
                ].map(([k, t]) => (
                  <li key={k} className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${t ? "bg-emerald-500" : "bg-zinc-300"}`} />
                    <span className="w-20 text-[10px] uppercase text-muted-foreground">{k}</span>
                    <span className="font-mono">{t ? new Date(t).toLocaleString() : "—"}</span>
                  </li>
                ))}
              </ol>
              {selected.failure_reason && (
                <div className="text-xs border border-red-300 bg-red-50/40 dark:bg-red-900/10 p-2 rounded-sm">
                  <b>Failure:</b> {selected.failure_code} · {selected.failure_reason}
                </div>
              )}
              <div className="text-[10px] text-muted-foreground">Meta message id: <span className="font-mono">{selected.meta_message_id || "—"}</span></div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
